/**
 * @file Recomputes the analytics service's `monthStarts` bound for a resolved
 * period: `period.from`, then the local midnight in `timeZone` of each later
 * month start strictly before `period.to`, as `Date#toISOString()` strings.
 * Consumer: `analyticsMonths`, whose month numbers count from 1 in this list,
 * exactly as the by-month queries do. It mirrors `monthStarts` in
 * `cloud_run/analytics/src/serving/resolveCalendarBound.ts`; a test pins both
 * to the same values.
 */
import { DateTime } from 'luxon';

import type { ResolvedAnalyticsPeriod } from '#types';

const MONTHS_PER_YEAR = 12;

export const analyticsMonthStarts = (
  period: Pick<ResolvedAnalyticsPeriod, 'from' | 'to'>,
  timeZone: string,
): string[] => {
  const start = DateTime.fromISO(period.from, { zone: timeZone });
  const end = DateTime.fromISO(period.to, { zone: timeZone });
  const later =
    (end.year - start.year) * MONTHS_PER_YEAR + end.month - start.month;
  return [
    start,
    ...Array.from({ length: Math.max(later, 0) }, (_, index) =>
      start.startOf('month').plus({ months: index + 1 }),
    ).filter((monthStart) => monthStart.toMillis() < end.toMillis()),
  ].map((instant) => instant.toJSDate().toISOString());
};
